import { useState } from 'react';
import { makeStyles } from '@material-ui/core';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';

const useStyles = makeStyles(theme => ({
  box: {
    alignSelf: 'flex-start',
    maxWidth: '100%',
    border: '1px solid var(--ui-warn-border)',
    borderRadius: 8,
    background: 'var(--ui-warn-bg)',
    color: 'var(--ui-text)',
    fontSize: '0.875rem',
  },
  toggle: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
    padding: theme.spacing(0.75, 1.5),
    border: 'none',
    borderRadius: 8,
    background: 'transparent',
    color: 'inherit',
    font: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    '&:focus-visible': {
      outline: '2px solid var(--ui-primary)',
      outlineOffset: 1,
    },
  },
  icon: { fontSize: '1.1rem', color: 'var(--ui-warn-border)' },
  chevron: {
    marginLeft: 'auto',
    color: 'var(--ui-text-muted)',
    transition: 'transform 120ms',
    '&[data-open="true"]': { transform: 'rotate(90deg)' },
  },
  list: {
    margin: 0,
    padding: theme.spacing(0, 1.5, 1, 4.75),
    lineHeight: 1.6,
  },
}));

/**
 * What a page could not count, folded into one line until the reader asks:
 * a warning sign and how much is missing, opening onto a sentence per source.
 * The table is what a reader came for; the reasons a few marks are "?" are
 * there for whoever wonders.
 */
export const CollapsedNotices = ({
  id,
  label,
  summary,
  notices,
}: {
  id: string;
  /** The list's name, as a screen reader announces it. */
  label: string;
  /** The one line shown while folded. */
  summary: string;
  notices: string[];
}) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);
  if (notices.length === 0) return null;
  return (
    <div className={classes.box}>
      <button
        type="button"
        className={classes.toggle}
        aria-expanded={open}
        aria-controls={id}
        onClick={() => setOpen(o => !o)}
      >
        <ReportProblemOutlinedIcon className={classes.icon} aria-hidden />
        <span>{summary}</span>
        <span className={classes.chevron} data-open={open} aria-hidden>
          ›
        </span>
      </button>
      {open && (
        <ul id={id} className={classes.list} aria-label={label}>
          {notices.map(notice => (
            <li key={notice}>{notice}</li>
          ))}
        </ul>
      )}
    </div>
  );
};
